<template>
  <div v-if="!field.hidden">
    <sui-form-fields>
      <sui-form-field :width="12" :required="field.required" :error="isInvalid || !!uploadError">
        <label>
          URL
          <span class="tooltip" data-inverted data-position="left center" :data-tooltip="field.warning" v-if="field.warning"><i class="warning sign yellow fitted icon" /></span>
          <span class="tooltip" data-inverted data-position="left center" :data-tooltip="tooltip" v-if="tooltip"><i class="help circle blue fitted icon" /></span>
        </label>
        <div class="ui labeled input" :class="{disabled: uploading}">
          <sui-input v-model.trim="$attrs.value.url" :readonly="field.readonly" :loading="uploading" :icon="icon" :placeholder="fieldPlaceholder" ref="url" @input="onChangeUrl()" @click="nextType($event)" />
        </div>
        <small v-if="!field.readonly">
          <label class="example upload"><input type="file" ref="file" accept=".tar.bz2, .tar.gz, .tar, .zip" @change="upload()"/>Upload archive</label>
          <span v-if="field.example">Example: <a class="example" :href="field.example" @click.prevent="insertExample()">{{field.example}}</a></span>
        </small>
      </sui-form-field>
      <sui-form-field :width="4">
        <label>Branch / Commit / Tag</label>
        <sui-input :class="{disabled: !$attrs.value.url || $attrs.value.type !== 'git'}" v-model.trim="$attrs.value.branch" :readonly="field.readonly" placeholder="master" ref="branch" />
      </sui-form-field>
    </sui-form-fields>
    <sui-form-fields>
      <sui-form-field :width="5">
        <label>
          SSH Access <small>(recommended)</small>
          <span class="tooltip" data-inverted data-tooltip="Add this key to your account or repository"><i class="help circle blue fitted icon" /></span>
        </label>
        <div class="ui tiny fluid button" :class="{disabled: !publicKeyValue || $attrs.value.type !== 'git'}" @click.prevent="copyKey()">
          {{publicKeyStatus}}
        </div>
        <small class="error" v-if="!publicKeyValue">{{publicKeyError}}&nbsp;<a href="#" @click.prevent="updateKey()">Try again</a></small>
      </sui-form-field>
      <sui-form-field :width="2" style="position: relative;">
        <sui-divider vertical>Or</sui-divider>
      </sui-form-field>
      <sui-form-field :width="4">
        <label>Login</label>
        <sui-input class="tiny" :class="{disabled: !$attrs.value.url || $attrs.value.type === 'upload'}" v-model.trim="$attrs.value.login" :readonly="field.readonly" ref="login" />
      </sui-form-field>
      <sui-form-field :width="5">
        <label>Password</label>
        <sui-input class="tiny" type="password" :class="{disabled: !$attrs.value.url || $attrs.value.type === 'upload'}" v-model.trim="$attrs.value.password" :readonly="field.readonly" ref="password" />
      </sui-form-field>
    </sui-form-fields>
  </div>
</template>

<script>
  import {mapGetters} from 'vuex'
  import FormField from '@/mixins/FormField'
  import api from '@/api'
  import utils from '@/utils'

  export default {
    name: 'FormServiceSource',
    mixins: [
      FormField,
    ],
    data() {
      return {
        changed: Boolean(this.$attrs.value && this.$attrs.value.url),
        publicKeyValue: '',
        publicKeyStatus: 'Copy SSH Key',
        publicKeyError: null,
        uploading: false,
        uploadError: null,
      }
    },
    computed: {
      ...mapGetters(['config']),
      icon() {
        if (!this.$attrs.value || !this.$attrs.value.url) {
          return 'linkify'
        }
        if (this.$attrs.value.type === 'git') {
          return 'link git'
        }
        if (this.$attrs.value.type === 'upload') {
          return 'link upload'
        }
        if (this.$attrs.value.type === 'download') {
          return 'link cloud download'
        }
        return 'linkify'
      },
      fieldPlaceholder() {
        const error = this.uploadError || (this.changed && this.error)
        return this.uploading ? 'Uploading...' : (error || 'https://')
      },
    },
    methods: {
      set(fields = {}) {
        if (!this.field.readonly) {
          this.uploadError = null
          this.$attrs.value = {...(this.$attrs.value || {}), ...fields}
          this.onInput()
        }
      },
      onChangeUrl() {
        const isArchiveUrl = utils.isArchiveUrl(this.$attrs.value.url)
        const isGitUrl = utils.isGitUrl(this.$attrs.value.url)
        const matchGitService = utils.matchGithub(this.$attrs.value.url) || utils.matchBitbucket(this.$attrs.value.url) || utils.matchGitlab(this.$attrs.value.url)
        const type = (!isArchiveUrl && (isGitUrl || matchGitService)) ? 'git' : 'download'
        let url = this.$attrs.value.url
        let branch = this.$attrs.value.branch || ''
        this.set({url, type, branch})
        if (matchGitService && matchGitService[4]) {
          setTimeout(() => {
            url = url.replace(new RegExp(`/?${matchGitService[3]}`), '')
            branch = matchGitService[4] || 'master'
            this.set({url, branch})
          }, 500)
        }
      },
      nextType(event) {
        if (event.target.localName === 'i') {
          this.set({type: this.$attrs.value.type === 'download' ? 'git' :'download'})
        }
      },
      updateKey() {
        this.publicKeyValue = ''
        this.publicKeyError = 'Loading...'
        utils.getPublicKey().then(key => {
          this.publicKeyError = 'Loading failed!'
          this.publicKeyValue = key.value
        })
      },
      copyKey() {
        this.$copyText(this.publicKeyValue).then(() => {
          this.publicKeyStatus = 'Copied!'
        }).catch(() => {
          this.publicKeyStatus = 'Can\'t copy!'
        })
        setTimeout(() => {
          this.publicKeyStatus = 'Copy SSH Key'
        }, 1000)
      },
      insertExample() {
        this.set({url: this.field.example, type: 'git'})
      },
      upload() {
        this.$attrs.value.url = ''
        const archive = this.$refs.file.files[0]
        if (!archive) {
          this.uploadError = 'File is not selected'
          this.$refs.url.$refs.input.focus()
          return false
        }
        const isArchive = utils.isArchiveUrl(archive.name)
        if (!isArchive) {
          this.uploadError = 'Available archive types: .tar.bz2, .tar.gz, .tar, .zip'
          this.$refs.url.$refs.input.focus()
          return false
        }
        const uploadSize = this.config.uploadSize
        const goodSize = archive.size < uploadSize
        if (!goodSize) {
          this.uploadError = 'Maximum file size: ' + utils.prettyBytes(uploadSize)
          this.$refs.url.$refs.input.focus()
          return false
        }
        this.uploading = true
        utils.reachGoal('SERVICE_FORM_SOURCES_UPLOAD')
        api.upload(archive)
          .finally(() => {
            this.uploading = false
            this.$refs.file.value = ''
          })
          .then(response => {
            this.set({url: response.data.result, type: 'upload'})
          })
          .catch(error => {
            this.set({type: 'upload'})
            this.uploadError = error
            this.$refs.url.$refs.input.focus()
          })
      }
    },
    mounted() {
      this.updateKey()
    },
  }
</script>

<style scoped>
  .tooltip { float: right; }
  .example { color: #48c; cursor: pointer; }
  .example:hover { color: #6ae; }
  .upload { float: right; padding: .2em; }
  .error { color: #9f3a38; }
</style>
